// Passage Quest: read a short passage, then answer questions that scale from recall to
// inference to evidence. Every answer comes with the sentence that proves it.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { PASSAGES, passageBandFor, type Passage, type PassageQuestion } from '@content/words/passages';

const PASSAGES_PER_ROUND = 2;

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  const band = passageBandFor(level);
  const passages: Passage[] = shuffle(ctx.rng, PASSAGES[band]).slice(0, PASSAGES_PER_ROUND);
  const totalQ = passages.reduce((n, p) => n + p.questions.length, 0);
  let pi = 0, qi = 0, done = 0;
  let attempts = 0, correct = 0;
  let missedThis = false;
  let locked = false;
  const t0 = performance.now();
  let tQ = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Question ', h('strong', null, '1'), ` of ${totalQ}`), h('button.btn.small.ghost', { onclick: () => ctx.speak(cur().text) }, 'Read it to me'));
  const title = h('h3', { style: { padding: '4px 0 0' } });
  const text = h('div.card', { style: { fontSize: '17px', lineHeight: '1.5', maxHeight: '38vh', overflowY: 'auto' } });
  const question = h('p.prompt', { style: { fontSize: '19px', lineHeight: '1.3', minHeight: '52px', textAlign: 'left' } });
  const choices = h('div.choices');
  const why = h('p.muted', { style: { minHeight: '44px', fontWeight: '700' } });
  host.append(head, title, text, question, choices, why);

  function cur(): Passage { return passages[pi]; }
  function curQ(): PassageQuestion { return cur().questions[qi]; }

  function showPassage() {
    title.textContent = cur().title;
    text.textContent = cur().text;
    text.scrollTop = 0;
  }

  function showQuestion() {
    const q = curQ();
    (head.querySelector('strong') as HTMLElement).textContent = String(done + 1);
    question.textContent = q.q;
    why.textContent = '';
    missedThis = false; locked = false;
    tQ = performance.now();
    clear(choices);
    for (const ci of shuffle(ctx.rng, [0, 1, 2, 3])) {
      const label = q.choices[ci];
      choices.append(h('button.choice', { onclick: () => choose(ci), style: { fontSize: label.length > 28 ? '14px' : label.length > 14 ? '16px' : '19px', padding: '8px 8px', textTransform: 'none', lineHeight: '1.25', gridColumn: label.length > 40 ? '1 / -1' : '' } }, label));
    }
  }

  function choose(ci: number) {
    if (locked) return;
    const q = curQ();
    const ms = performance.now() - tQ;
    const btns = [...choices.querySelectorAll('button')] as HTMLButtonElement[];
    const btn = btns.find(b => b.textContent === q.choices[ci]);
    const id = `passage:${band}:${cur().title.slice(0, 12)}:${qi}`;
    if (ci === q.answer) {
      locked = true;
      if (!missedThis) { attempts++; correct++; }
      ctx.report({ skill: 'comprehension', level, correct: !missedThis, hintUsed: missedThis, ms, problemId: id });
      ctx.sound('ding');
      btn?.classList.add('good');
      why.textContent = q.why;
      for (const b of btns) b.disabled = true;
      setTimeout(next, 2200);
    } else {
      if (!missedThis) { attempts++; missedThis = true; ctx.report({ skill: 'comprehension', level, correct: false, hintUsed: false, ms, problemId: id }); }
      ctx.sound('buzz');
      if (btn) { btn.classList.add('bad'); btn.disabled = true; }
      why.textContent = 'Not that one. Look back at the passage for the sentence that answers it.';
    }
  }

  function next() {
    done++;
    qi++;
    if (qi >= cur().questions.length) {
      pi++; qi = 0;
      if (pi >= passages.length) {
        if (attempts === correct && level >= 5) ctx.charm('charm.reader');
        ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Passages read: ${passages.length}`] });
        return;
      }
      level = ctx.currentLevel();
      showPassage();
      ctx.say('A new passage. Read it first, then the questions.');
    }
    showQuestion();
  }

  showPassage();
  showQuestion();
  ctx.say('Read the passage, then answer. You can read it again any time.');
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.passage-quest',
  title: 'Passage Quest',
  blurb: 'Read a short passage and answer questions. The proof is always in the text.',
  category: 'words',
  skills: ['comprehension'],
  levelRange: [1, 10],
  unlock: { stars: 10 },
  mount,
};
export default mod;
